/*!
 * TinyPoker Excel 上傳組件
 * 提供週報 Excel 文件上傳功能
 */
import React, { useState, useRef, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Progress,
} from "reactstrap";
import axios from "axios";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import { TinyBusy } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

// 文件類型說明
const FILE_TYPES = {
  member_daily: {
    name: "會員日報表",
    pattern: /member.*daily/i,
    required: true,
    description: "包含每日會員數據的報表",
  },
  member_summary: {
    name: "會員週總表",
    pattern: /member.*summary/i,
    required: true,
    description: "包含週會員匯總數據的報表",
  },
  club_daily: {
    name: "俱樂部日報表",
    pattern: /club.*daily/i,
    required: false,
    description: "包含每日俱樂部數據的報表",
  },
  mtt_report: {
    name: "MTT 報表",
    pattern: /mtt/i,
    required: false,
    description: "MTT 錦標賽數據報表",
  },
  insurance_report: {
    name: "保險報表",
    pattern: /insurance/i,
    required: false,
    description: "保險相關數據報表",
  },
};

function TinyUpload() {
  // State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [weekRange, setWeekRange] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // 生成本週週期
  const generateCurrentWeekRange = useCallback(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    // 計算本週一和週日
    const monday = new Date(today);
    monday.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}${m}${d}`;
    };

    return `${formatDate(monday)}-${formatDate(sunday)}`;
  }, []);

  // 初始化週期
  React.useEffect(() => {
    if (!weekRange) {
      setWeekRange(generateCurrentWeekRange());
    }
  }, [weekRange, generateCurrentWeekRange]);

  // 處理文件選擇
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  // 處理拖放
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    }
  };

  // 處理文件
  const processFiles = (files) => {
    const excelFiles = files.filter(
      (f) =>
        f.name.endsWith(".xlsx") ||
        f.name.endsWith(".xls") ||
        f.name.endsWith(".csv")
    );

    // 識別文件類型
    const processedFiles = excelFiles.map((file) => {
      let fileType = "unknown";
      for (const [type, config] of Object.entries(FILE_TYPES)) {
        if (config.pattern.test(file.name)) {
          fileType = type;
          break;
        }
      }
      return {
        file,
        name: file.name,
        size: file.size,
        type: fileType,
        status: "pending",
      };
    });

    setSelectedFiles((prev) => [...prev, ...processedFiles]);
    setError(null);
    setUploadResult(null);
  };

  // 移除文件
  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // 清除所有文件
  const clearFiles = () => {
    setSelectedFiles([]);
    setUploadResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 上傳文件
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("請選擇要上傳的文件");
      return;
    }

    if (!weekRange || !/^\d{8}-\d{8}$/.test(weekRange)) {
      setError("請輸入正確的週期格式 (YYYYMMDD-YYYYMMDD)");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append("week", weekRange);
    selectedFiles.forEach((fileInfo, index) => {
      formData.append(`file_${index}`, fileInfo.file);
      formData.append(`file_${index}_type`, fileInfo.type);
    });

    try {
      const res = await axios.post(`${API_BASE}/upload/excel`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (res.data.code === 1) {
        setUploadResult({
          success: true,
          message: res.data.data.message || "上傳成功",
          details: res.data.data,
        });
        // 更新文件狀態
        setSelectedFiles((prev) =>
          prev.map((f) => ({ ...f, status: "uploaded" }))
        );
      } else {
        throw new Error(res.data.msg);
      }
    } catch (err) {
      console.error("上傳失敗:", err);
      setError("上傳失敗: " + (err.response?.data?.msg || err.message));
      setUploadResult({
        success: false,
        message: err.response?.data?.msg || err.message,
      });
    } finally {
      setUploading(false);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // 獲取文件類型 Badge 顏色
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "member_daily":
        return "success";
      case "member_summary":
        return "primary";
      case "club_daily":
        return "info";
      case "mtt_report":
        return "warning";
      case "insurance_report":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div className="content">
      {/* 這頁原本沒有 page header（「上傳週報 Excel 文件」是上傳區的區塊標題）。 */}
      <TinyPageHeader
        title="上傳 Excel"
        description="上傳 ClubGG 匯出的週報 Excel，作為所有報表與分潤計算的資料來源。"
      />

      {/* 錯誤/成功提示 */}
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {uploadResult && (
        <Alert
          color={uploadResult.success ? "success" : "danger"}
          toggle={() => setUploadResult(null)}
        >
          <strong>{uploadResult.success ? "✓ 成功" : "✗ 失敗"}</strong>
          <br />
          {uploadResult.message}
          {uploadResult.details?.files_processed && (
            <div className="mt-2">
              <small>
                處理文件數: {uploadResult.details.files_processed}
                <br />
                導入記錄數: {uploadResult.details.records_imported || 0}
              </small>
            </div>
          )}
        </Alert>
      )}

      <Row>
        {/* 上傳區域 */}
        <Col lg="8">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                <i className="fa fa-upload mr-2" />
                上傳週報 Excel 文件
              </CardTitle>
            </CardHeader>
            <CardBody>
              {/* 週期輸入 */}
              <Row className="mb-4">
                <Col md="6">
                  <FormGroup>
                    <Label>週期 (Week Range)</Label>
                    <Input
                      type="text"
                      value={weekRange}
                      onChange={(e) => setWeekRange(e.target.value)}
                      placeholder="YYYYMMDD-YYYYMMDD"
                    />
                    <small className="text-muted">
                      格式: 起始日期-結束日期，例如 20260105-20260111
                    </small>
                  </FormGroup>
                </Col>
                <Col md="6" className="d-flex align-items-end">
                  <Button
                    color="secondary"
                    outline
                    onClick={() => setWeekRange(generateCurrentWeekRange())}
                  >
                    <i className="fa fa-calendar mr-1" />
                    使用本週
                  </Button>
                </Col>
              </Row>

              {/* 拖放區域 */}
              <div
                className={`upload-dropzone p-5 text-center border border-dashed rounded ${
                  dragActive ? "bg-primary-light border-primary" : "bg-light"
                }`}
                style={{
                  borderStyle: "dashed",
                  borderWidth: "2px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor: dragActive ? "#e3f2fd" : "#f8f9fa",
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <i
                  className="fa fa-cloud-upload fa-3x mb-3"
                  style={{ color: dragActive ? "#1976d2" : "#6c757d" }}
                />
                <h5>{dragActive ? "放開以上傳文件" : "拖放文件到此處"}</h5>
                <p className="text-muted mb-0">
                  或點擊此處選擇文件
                  <br />
                  <small>支持 .xlsx, .xls, .csv 格式</small>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
              </div>

              {/* 已選文件列表 */}
              {selectedFiles.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">
                      已選擇 {selectedFiles.length} 個文件
                    </h5>
                    <Button color="link" size="sm" onClick={clearFiles}>
                      清除全部
                    </Button>
                  </div>
                  <Table responsive size="sm">
                    <thead>
                      <tr>
                        <th>文件名</th>
                        <th>類型</th>
                        <th>大小</th>
                        <th>狀態</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFiles.map((fileInfo, index) => (
                        <tr key={index}>
                          <td>
                            <i className="fa fa-file-excel text-success mr-2" />
                            {fileInfo.name}
                          </td>
                          <td>
                            <Badge color={getTypeBadgeColor(fileInfo.type)}>
                              {FILE_TYPES[fileInfo.type]?.name || "未識別"}
                            </Badge>
                          </td>
                          <td>
                            <small>{formatFileSize(fileInfo.size)}</small>
                          </td>
                          <td>
                            {fileInfo.status === "uploaded" ? (
                              <Badge color="success">
                                <i className="fa fa-check mr-1" />
                                已上傳
                              </Badge>
                            ) : fileInfo.status === "uploading" ? (
                              <TinyBusy text="上傳中" />
                            ) : (
                              <Badge color="secondary">待上傳</Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              color="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => removeFile(index)}
                              disabled={uploading}
                            >
                              <i className="fa fa-times" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* 上傳進度 */}
              {uploading && (
                <div className="mt-3">
                  <Progress
                    value={uploadProgress}
                    color="primary"
                    striped
                    animated
                  >
                    {uploadProgress}%
                  </Progress>
                </div>
              )}

              {/* 上傳按鈕 */}
              <div className="mt-4 text-center">
                <Button
                  color="primary"
                  size="lg"
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? (
                    <>
                      <TinyBusy light style={{ marginRight: 8 }} />
                      上傳中
                    </>
                  ) : (
                    <>
                      <i className="fa fa-upload mr-2" />
                      開始上傳
                    </>
                  )}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* 說明區域 */}
        <Col lg="4">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-info-circle mr-2" />
                文件類型說明
              </CardTitle>
            </CardHeader>
            <CardBody>
              {Object.entries(FILE_TYPES).map(([type, config]) => (
                <div key={type} className="mb-3">
                  <Badge color={getTypeBadgeColor(type)} className="mb-1">
                    {config.name}
                    {config.required && (
                      <span className="ml-1 text-white">*</span>
                    )}
                  </Badge>
                  <p className="text-muted small mb-0">{config.description}</p>
                </div>
              ))}
              <hr />
              <small className="text-muted">
                <i className="fa fa-exclamation-circle mr-1" />
                標記 * 的為必要文件
              </small>
            </CardBody>
          </Card>

          <Card className="mt-3">
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-question-circle mr-2" />
                上傳流程
              </CardTitle>
            </CardHeader>
            <CardBody>
              <ol className="pl-3 mb-0">
                <li className="mb-2">
                  <small>輸入或選擇週期 (Week Range)</small>
                </li>
                <li className="mb-2">
                  <small>拖放或選擇 Excel 文件</small>
                </li>
                <li className="mb-2">
                  <small>確認文件類型識別正確</small>
                </li>
                <li className="mb-2">
                  <small>點擊「開始上傳」按鈕</small>
                </li>
                <li>
                  <small>上傳完成後，前往「週報生成」頁面執行報表生成</small>
                </li>
              </ol>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default TinyUpload;


